/**
 * ActivatePage (Jose 2026-10-10; manual-entry fallback 2026-10-12) — "/activate".
 * Two ways in, same redeem:
 *   1. "/activate?code=XXXX" → auto-redeems the one-time link (texted by the owner).
 *   2. "/activate" with no code → shows a box to TYPE the activation code. This is the bulletproof path
 *      when the deep-link gets mangled by a messaging app and the barber only ever lands on the login page
 *      (Diana, 2026-10-12): she reaches /login reliably, taps "enter your code", types a short code → in.
 * Redeem mints a Firebase custom token (redeemStationActivation) → signs THIS device into the shared
 * station account (no shop password). Then the app shows the PIN pad. Links/codes are single-use + 24h.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { app, auth } from '../config/firebase';
import { Loader2, CheckCircle2, AlertTriangle, KeyRound } from 'lucide-react';

const fns = getFunctions(app); // default region us-central1 (matches the deployed function)

const ActivatePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [status, setStatus] = useState<'idle' | 'working' | 'ok' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [codeInput, setCodeInput] = useState('');

  const doRedeem = async (raw: string) => {
    const code = (raw || '').replace(/\s+/g, ''); // forgive spaces so "4827 1536" matches "48271536"
    if (!code) { setStatus('error'); setMsg('Enter the activation code the owner gave you.'); return; }
    setStatus('working'); setMsg('Activating this device…');
    try {
      const redeem = httpsCallable(fns, 'redeemStationActivation', { timeout: 20_000 });
      const res: any = await redeem({ code });
      const token = res?.data?.token;
      if (!token) throw new Error('no-token');
      await signInWithCustomToken(auth, token);
      setStatus('ok'); setMsg('Device activated! Taking you in…');
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (e: any) {
      const m = String(e?.message || '');
      setStatus('error');
      setMsg(
        /expired/i.test(m) ? 'This code has expired — ask the owner for a new one.' :
        /already used/i.test(m) ? 'This code was already used — ask the owner for a new one.' :
        /not.?found|invalid/i.test(m) ? 'That code isn’t valid — double-check it, or ask the owner for a new one.' :
        'Couldn’t activate this device. Check the code or ask the owner for a new one.'
      );
    }
  };

  useEffect(() => {
    if (ran.current) return; // one-time: a redeem is single-use
    ran.current = true;
    const code = params.get('code');
    if (code) doRedeem(code);           // link path → auto-redeem
    // no code → stay 'idle' and show the type-it-in box
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const Icon = status === 'ok' ? CheckCircle2 : status === 'error' ? AlertTriangle : status === 'working' ? Loader2 : KeyRound;
  const color = status === 'ok' ? 'text-emerald-400' : status === 'error' ? 'text-amber-400' : 'text-white';
  const showForm = status === 'idle' || status === 'error';

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#2A211A 0%,#1C1A17 100%)' }}>
      <div className="max-w-sm w-full text-center text-white">
        <div className="text-3xl font-black tracking-tight mb-6">SLIK</div>
        <Icon className={`h-12 w-12 mx-auto ${color} ${status === 'working' ? 'animate-spin' : ''}`} />
        <p className="mt-4 text-lg font-semibold leading-snug">
          {status === 'idle' ? 'Set up this device' : msg}
        </p>

        {showForm && (
          <form onSubmit={(e) => { e.preventDefault(); doRedeem(codeInput); }} className="mt-6 text-left">
            <label className="block text-sm text-white/70 mb-2">Enter your activation code</label>
            <input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              autoFocus
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="e.g. 4827 1536"
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center text-xl tracking-widest placeholder-white/30 focus:outline-none focus:border-white/50"
            />
            <button
              type="submit"
              className="mt-4 w-full px-5 py-3 min-h-[48px] rounded-xl bg-[#C0824A] hover:bg-[#a96f3c] text-white font-semibold active:scale-95 transition"
            >
              Activate this device
            </button>
            <p className="mt-4 text-xs text-white/50 text-center">Ask the owner (Settings → Shop Access) to generate your code.</p>
          </form>
        )}

        {status === 'error' && (
          <button onClick={() => navigate('/login')} className="mt-5 text-sm text-white/60 underline">
            Back to login
          </button>
        )}
      </div>
    </div>
  );
};

export default ActivatePage;
